/**
 * AgentActionRegistry
 *
 * Registry for action handlers that the Platform Assistant can invoke.
 * Maps action IDs to handler functions and executes them when requested.
 */

import type { ActionHandler, ActionRegistration, ActionResult } from '../types';

/**
 * Registry for managing action handlers.
 *
 * Responsibilities:
 * - Store action handlers from useAgentAction hooks
 * - Look up handlers by action_id
 * - Execute handlers with provided parameters
 * - Return execution results
 */
export class AgentActionRegistry {
  private handlers: Map<string, ActionHandler> = new Map();

  /**
   * Register an action handler
   */
  register(registration: ActionRegistration): void {
    if (this.handlers.has(registration.action_id)) {
      console.warn(
        `[AgentActionRegistry] Overwriting existing handler for action: ${registration.action_id}`
      );
    }
    this.handlers.set(registration.action_id, registration.handler);
  }

  /**
   * Unregister an action handler
   */
  unregister(actionId: string): void {
    this.handlers.delete(actionId);
  }

  /**
   * Check if a handler exists for an action
   */
  hasHandler(actionId: string): boolean {
    return this.handlers.has(actionId);
  }

  /**
   * Get a handler by action ID
   */
  getHandler(actionId: string): ActionHandler | undefined {
    return this.handlers.get(actionId);
  }

  /**
   * Execute an action with the given parameters
   *
   * @param actionId - The action to execute
   * @param params - Parameters including element_id
   * @returns ActionResult with success status
   */
  async execute(
    actionId: string,
    params: Record<string, unknown>
  ): Promise<ActionResult> {
    const elementId = (params.element_id as string) || 'unknown';

    const handler = this.handlers.get(actionId);
    if (!handler) {
      console.error(
        `[AgentActionRegistry] No handler found for action: ${actionId}`
      );
      return {
        action_id: actionId,
        element_id: elementId,
        success: false,
        error: `No handler registered for action: ${actionId}`,
      };
    }

    try {
      await handler(params);
      return {
        action_id: actionId,
        element_id: elementId,
        success: true,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(
        `[AgentActionRegistry] Action execution failed: ${actionId}`,
        error
      );
      return {
        action_id: actionId,
        element_id: elementId,
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Execute multiple actions sequentially
   *
   * @param actions - Array of actions to execute
   * @param onBeforeAction - Callback before each action (for highlighting)
   * @param onAfterAction - Callback after each action
   * @returns Array of ActionResults
   */
  async executeMultiple(
    actions: Array<{ action_id: string; params: Record<string, unknown> }>,
    onBeforeAction?: (
      actionId: string,
      params: Record<string, unknown>
    ) => Promise<void>,
    onAfterAction?: (result: ActionResult) => Promise<void>
  ): Promise<ActionResult[]> {
    const results: ActionResult[] = [];

    for (const action of actions) {
      // Pre-action callback (e.g., highlight)
      if (onBeforeAction) {
        await onBeforeAction(action.action_id, action.params);
      }

      // Execute the action
      const result = await this.execute(action.action_id, action.params);
      results.push(result);

      // Post-action callback
      if (onAfterAction) {
        await onAfterAction(result);
      }

      // Brief pause between actions for visual feedback
      if (actions.indexOf(action) < actions.length - 1) {
        await this.delay(200);
      }
    }

    return results;
  }

  /**
   * Get all registered action IDs
   */
  getRegisteredActions(): string[] {
    return Array.from(this.handlers.keys());
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers.clear();
  }

  /**
   * Utility: delay for a given number of milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
